import {BaseFilterModel} from '../model/filter/BaseFilterModel.js';
import {CacheUtils as Cache} from '../utils/CacheUtils';

/**
 * Basis for all other collection. Can be used as is only for fully client-side collection.
 *
 * @class BaseCollection
 * @extends Backbone.Collection
 *
 * @property {number}  length
 * @property {object}  options
 * @property {boolean} isUpdating
 * @property {boolean} isNeedUpdate
 */
export class BaseCollection extends Backbone.Collection {

    get model () {

        null;
    }

    static get SORT_DIR () {

        return {
            ASC: '+',
            DESC: '-'
        };
    }

    static get SORT_TYPE () {

        return {
            SERVER: 'SERVER',
            CLIENT: 'CLIENT'
        };
    }

    static get EVENTS () {

        return {
            UPD_START: 'update:start',
            UPD_COMPL: 'update:complete',
            CHANGED_FILTER: 'change:filterparams',
            CHANGED_SELECTED: 'change:selected'
        };
    }

    set microservice (name) {

        this._microservice = name;
        return this;
    }

    get microservice () {

        return this._microservice;
    }

    set resource (name) {

        this._resource = name;
        return this;
    }

    get resource () {

        return this._resource;
    }

    set isUpdating (isUpdating) {

        this._isUpdating = (_.isBoolean(isUpdating) ? isUpdating : false);
        return this;
    }

    get isUpdating () {

        return this._isUpdating;
    }

    set isNeedUpdate (isUpdating) {

        this._isNeedUpdate = (_.isBoolean(isUpdating) ? isUpdating : false);
        return this;
    }

    get isNeedUpdate () {

        return this._isNeedUpdate;
    }

    get url () {

        return (API_PREFIX + '/' + this.microservice + '/' + this.resource);
    }

    initialize (models, options) {

        this.isUpdating = false;
        this.isNeedUpdate = false;

        this.options = _.extend({
            page: 1,
            total: 1,
            order: '',
            filterParams: null,
            pageCapacity: 20,
            selectedItems: []
        }, options);
    }

    onSelectedChange () {

        this.options.selectedItems = this.where({
            selected: true
        });
    }

    setSelected (isSelected) {

        this.forEach(function(item) {
            item.setSelected(isSelected);
        });

        this.trigger(BaseCollection.EVENTS.CHANGED_SELECTED);
    }

    setPage (pageToSet) {

        pageToSet = parseInt(pageToSet, 10);

        if (pageToSet && pageToSet > 0) {

            this.options.page = pageToSet;
            return this.update(this.options);
        }

        return false;
    }

    setPageCapacity (pageCapacity) {

        pageCapacity = parseInt(pageCapacity, 10);

        if (pageCapacity && pageCapacity > 0 && pageCapacity !== this.options.pageCapacity) {
            this.options.pageCapacity = pageCapacity;
            this.setPage(1);
        }
    }

    setSortField (sortField, sortType) {

        if (sortType === BaseCollection.SORT_TYPE.CLIENT) {

            this.trigger(BaseCollection.EVENTS.UPD_START);

            let sortIndex = ((sortField.substring(0, 1) === BaseCollection.SORT_DIR.DESC) ? -1 : 1);
            let sortKey = sortField.substring(1);

            this.options.order = sortField;
            this.comparator = this.createComparator(sortKey, sortIndex);

            this.sort();
            this.trigger(BaseCollection.EVENTS.UPD_COMPL);
        } else {

            if (sortField && typeof sortField === 'string' && sortField != this.options.order) {
                this.options.order = sortField;
                this.setPage(1);
            }
        }
    }

    dropFilterParams () {

        if (this.options && this.options.filterParams) {
            this.options.filterParams = null;
        }

        this.reset();
    }

    /**
     * @param {BaseFilterModel} filterParams
     * @return {$.Deferred} Promise that will be resolved on collection update finished
     */
    setFilterParams (filterParams) {

        if (filterParams instanceof BaseFilterModel) {

            this.options.filterParams = filterParams;
            this.trigger(BaseCollection.EVENTS.CHANGED_FILTER);

            return this.setPage(1);
        }

        return false;
    }

    getFilterParams () {

        return this.options.filterParams;
    }

    hasFilterParams () {

        return (this.options.filterParams instanceof BaseFilterModel);
    }

    getItemsKeys () {

        let idAttribute = '';
        let keys = this.map(function(item) {

            if (!idAttribute) {
                if (_.isFunction(item.idAttribute)) {
                    idAttribute = item.idAttribute();
                }
                else {
                    idAttribute = (item.idAttribute ? item.idAttribute : 'id');
                }
            }

            return item.get(idAttribute);
        });

        return keys.join(',');
    }

    getFetchParams (page = 0, pageCapacity = 0) {

        this.setPage(page);
        this.setPageCapacity(pageCapacity);

        let fetchParams = {
            pageCapacity: this.options.pageCapacity,
            page: this.options.page
        };

        if (this.options.order) {
            fetchParams.order = this.options.order;
        }

        return fetchParams;
    }

    getPagingOptions () {

        return {
            page: this.options.page,
            total: this.options.total,
            order: this.options.order,
            pageCapacity: this.options.pageCapacity
        };
    }

    /**
     * @typedef {Object} Paging
     *
     * @property {number} page - current page number
     * @property {number} total - total number of pages
     * @property {number} pageCapacity - number of items in a page
     * @property {string} order - order of items in the page
     *
     */

    /**
     * @description This function returns the same promise for subsequent calls,
     * and refetches the collection when the first fetch is successfull.
     *
     * @event update:start - fetch requested
     * @event update:complete - fetch is successfull
     * @event update:fail - fetch failed
     *
     * @param {Paging} options - see this.getPagingOptions()
     * @param {boolean} disableAutoUpdate - disables refetching the collection
     *
     * @returns {jQuery.Deferred}
     */
    update (options = {}, disableAutoUpdate = false) {

        let filterParams = this.getFilterParams();

        if (this.isUpdating) {
            this.isNeedUpdate = true;

            return this.updatePromise;
        }

        this.isUpdating = true;

        if (!this.updatePromise
            || this.updatePromise.state() === 'resolved'
            || this.updatePromise.state() === 'rejected') {

            this.updatePromise = $.Deferred();
        }

        this.trigger('update:start');
        if(filterParams && options){
            options.data = filterParams.serializeAsQuery();
        }

        this.fetch(options)
            .then(() => {
                this.isUpdating = false;

                if (this.isNeedUpdate) {
                    this.isNeedUpdate = false;
                    if(disableAutoUpdate){
                        this.updatePromise.resolve(this);
                        this.trigger('update:complete');
                    } else {
                        this.update();
                    }
                } else {
                    this.updatePromise.resolve(this);
                    this.trigger('update:complete');
                }
            },(error) => {
                this.isUpdating = false;
                this.isNeedUpdate = false;
                this.updatePromise.reject(error);
                this.trigger('update:fail');
            });

        return this.updatePromise;
    }

    // @FIXME: Disabled caching until a fix is found
    /**
     * extend backbones fetch with caching
     *
     * if the cache is used the model might change after the initial promise resolution
     * it is required to listen on model change events to assert the right data is displayed
     *
     * @param {*} options
     * @return {JQuery.Deferred}
     */
    /*fetch (options) {

        if (Cache.has(this.url, options) && options && options.useCache === true) {
            const def = $.Deferred();

            super.fetch(options).then((data) => {
                Cache.put(this.url, data, options);
            });

            return def.resolve(Cache.get(this.url, options));
        } else {
            return super.fetch(options).then((data) => {
                Cache.put(this.url, data, options);
            });
        }
    }*/

    /**
     * @description will set collection paging options depends on URL values
     * @param {Object} options set of pagination and filter properties
     */
    mapQueryOptions (options) {

        if (options) {

            this.mapPagingQueryOptions(options);
            this.mapFilterQueryOptions(options);
        }
    }

    mapPagingQueryOptions (options) {

        var optsToSet = {};

        if (options.pageCapacity) {
            optsToSet.pageCapacity = parseInt(options.pageCapacity, 10);

            if (optsToSet.pageCapacity <= 0) {
                optsToSet.pageCapacity = this.options.pageCapacity;
            }
        }

        if (options.page) {
            optsToSet.page = parseInt(options.page, 10);

            if (optsToSet.page <= 0) {
                optsToSet.page = 1;
            }
        }

        if (options.order) {
            optsToSet.order = options.order;
        }

        this.options = _.extend(this.options, optsToSet);
    }

    parse (data) {

        if (_.isUndefined(data.items) && _.isArray(data)) {

            return data;
        }

        return data.items;
    }

    mapFilterQueryOptions () {

        this.options.filterParams = null;
    }

    getSelected () {

        return this.options.selectedItems;
    }

    createComparator (sortKey, sortIndex) {

        return function (item, nextItem) {

            let value1 = item.get(sortKey);
            let value2 = nextItem.get(sortKey);

            if (value1 < value2) {
                return sortIndex * -1;
            }

            if (value1 > value2) {
                return sortIndex * 1;
            }

            return 0;
        };
    }

    /**
     * @returns {[object]}
     */
    serialize () {

        return this.map((item) => item.serialize());
    }

    /**
     * @description extends clone function to optionally clone models inside the collection
     *
     * @param {boolean} deep - if true, models inside the collection will be cloned
     *                         together with the collection
     */
    clone(deep) {

        if(deep){
            let models = this.models.map((model) => model.clone());

            return new this.constructor(models, {
                model: this.model,
                comparator: this.comparator
            });
        } else {
            return super.clone();
        }
    }

    static cmpByDate (date1, date2, asc = true) {

        let result = 0;

        if (date1.isAfter(date2)) {
            result = 1;
        }
        else if (date1.isBefore(date2)) {
            result = -1;
        }
        else {
            result = 0;
        }

        if (!asc) {
            result = (result * -1);
        }

        return result;
    }
}



// WEBPACK FOOTER //
// ./shared/collection/BaseCollection.js